import { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { driverJobs, notifyDelivery, submitPod } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SignaturePad } from "@/components/SignaturePad";
import { InstallPrompt } from "@/components/InstallPrompt";
import { StatusPill, PriorityPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  MapPin, Clock, Truck, Package, LogOut, BellRing, CheckCircle2, Loader2,
  Camera, ImageIcon, X, PackageCheck, FileText, BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";
import { bolPublicUrl } from "@/lib/api";

const fmtTime = (v) => (v ? String(v).slice(11, 16) || "" : "");
const fmtDate = (v) => (v ? String(v).slice(0, 10) : "");
const isDone = (r) => r.status === "Completed" || r.delivered;

// downscale + compress an image file to a jpeg data URL
const fileToDataUrl = (file, max = 1000, quality = 0.7) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > max || height > max) {
          const s = max / Math.max(width, height);
          width = Math.round(width * s);
          height = Math.round(height * s);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function DriverPortal() {
  const qc = useQueryClient();
  const { user, logout } = useAuth();
  const { data: jobs = [], isLoading } = useQuery({ queryKey: ["driver-jobs"], queryFn: driverJobs });

  const [podFor, setPodFor] = useState(null); // job object
  const [signedBy, setSignedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState(null);
  const sigRef = useRef(null);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const { active, completed } = useMemo(() => {
    const sorted = [...jobs].sort((a, b) => String(a.estimated_start || "").localeCompare(String(b.estimated_start || "")));
    return {
      active: sorted.filter((r) => !isDone(r)),
      completed: sorted.filter(isDone),
    };
  }, [jobs]);

  const notifyMut = useMutation({
    mutationFn: (id) => notifyDelivery(id),
    onSuccess: (res) => {
      if (res?.no_email) toast.warning("No consignee/requester email on file for this job.");
      else toast.success(`Delivery notification sent to ${res.notified?.length || 0} recipient(s).`);
      qc.invalidateQueries({ queryKey: ["driver-jobs"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to send notification"),
  });

  const podMut = useMutation({
    mutationFn: ({ id, body }) => submitPod(id, body),
    onSuccess: (doc) => {
      if (doc?.pod_email_none) toast.success("Delivery completed. (No consignee/requester email on file for confirmation.)");
      else toast.success(`Delivery completed. Proof of Delivery emailed to ${doc?.pod_email_sent?.length || 0} recipient(s).`);
      qc.invalidateQueries({ queryKey: ["driver-jobs"] });
      closePod();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to submit POD"),
  });

  const openPod = (job) => {
    setPodFor(job);
    setSignedBy("");
    setNotes("");
    setPhoto(null);
  };
  const closePod = () => setPodFor(null);

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try { setPhoto(await fileToDataUrl(file)); }
    catch { toast.error("Could not read that image"); }
  };

  const submitPodForm = () => {
    if (!signedBy.trim()) { toast.error("Signee full name is required"); return; }
    if (sigRef.current?.isEmpty()) { toast.error("A signature is required"); return; }
    podMut.mutate({
      id: podFor.id,
      body: {
        signed_by: signedBy.trim(),
        signature: sigRef.current?.toDataURL(),
        notes: notes.trim() || null,
        photo,
      },
    });
  };

  const JobCard = ({ r, done }) => (
    <Card data-testid={`portal-job-${r.id}`} className="p-4 border-border/60 shadow-sm">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono-data font-semibold text-[#0055FF]">{r.work_order_no || r.requisition_no}</span>
          <span className="text-sm font-medium truncate">{r.project_name}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <PriorityPill value={r.priority} />
          <StatusPill value={r.status} />
        </div>
      </div>
      <div className="space-y-2 text-sm text-neutral-700">
        <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-neutral-400 mt-0.5 shrink-0" /><span>{r.pickup_location || "?"} → {r.delivery_location || "?"}</span></div>
        <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-neutral-400 shrink-0" />{fmtDate(r.estimated_start) || fmtDate(r.requested_pickup_date) || "—"} {fmtTime(r.estimated_start) && `· ${fmtTime(r.estimated_start)}–${fmtTime(r.estimated_end)}`}</div>
        <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-neutral-400 shrink-0" />{[r.truck_type, r.truck_unit_number].filter(Boolean).join(" #") || "—"}{r.trailer_number ? ` + ${r.trailer_type} #${r.trailer_number}` : ""}</div>
        <div className="flex items-start gap-2"><Package className="h-4 w-4 text-neutral-400 mt-0.5 shrink-0" /><span>{r.load_description || "—"}</span></div>
        {r.special_instructions && <div className="text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-amber-800">⚠ {r.special_instructions}</div>}
        {done && r.pod_signed_by && (
          <div className="text-xs bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 text-emerald-800 flex items-center gap-1.5">
            <BadgeCheck className="h-3.5 w-3.5 shrink-0" />
            <span>Delivered · signed by <b>{r.pod_signed_by}</b>{r.pod_at ? ` on ${fmtDate(r.pod_at)} ${fmtTime(r.pod_at)}` : ""}</span>
          </div>
        )}
      </div>
      {done && r.pod_signed_by && r.bol_token && (
        <a href={bolPublicUrl(r.id, r.bol_token)} target="_blank" rel="noopener noreferrer"
          data-testid={`view-bol-${r.id}`} className="mt-3 w-full inline-flex items-center justify-center gap-2 h-9 rounded-md border border-border/60 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors">
          <FileText className="h-4 w-4" /> View signed BOL
        </a>
      )}
      {!done && (
        <div className="flex gap-2 mt-4">
          <Button size="sm" variant="outline" data-testid={`notify-btn-${r.id}`}
            disabled={notifyMut.isPending} onClick={() => notifyMut.mutate(r.id)}
            className="flex-1 gap-2 border-border/60">
            <BellRing className="h-4 w-4" /> Notify Delivery
          </Button>
          <Button size="sm" data-testid={`complete-btn-${r.id}`} onClick={() => openPod(r)}
            className="flex-1 gap-2 bg-[#0055FF] hover:bg-[#0044CC]">
            <CheckCircle2 className="h-4 w-4" /> Complete
          </Button>
        </div>
      )}
    </Card>
  );

  return (
    <div className="min-h-screen bg-[#FDFDFD]">
      <header className="sticky top-0 z-20 bg-[#0A0A0A] text-white">
        <div className="px-4 h-14 flex items-center justify-between gap-3 max-w-2xl mx-auto">
          <div className="flex items-center gap-2.5 min-w-0">
            <img src="/ghost-logo.png" alt="Casper" className="h-8 w-8 rounded-md" />
            <div className="leading-tight min-w-0">
              <div className="font-display font-extrabold tracking-tight text-sm">GHOST TMS · Driver</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 truncate">{user?.name || user?.email}</div>
            </div>
          </div>
          <button data-testid="portal-logout-btn" onClick={logout} title="Sign out"
            className="text-white/60 hover:text-white p-2 rounded hover:bg-white/10">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <main className="px-4 py-6 max-w-2xl mx-auto space-y-6 animate-fade-up" data-testid="driver-portal">
        <InstallPrompt />
        {isLoading ? (
          <div className="text-center py-16 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : (
          <>
            <section>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                My Deliveries {active.length > 0 && <span className="text-[#0055FF]">({active.length})</span>}
              </h2>
              {active.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground" data-testid="portal-no-active">
                  <PackageCheck className="h-10 w-10 mx-auto mb-3 text-neutral-300" />
                  No active deliveries assigned to you.
                </div>
              ) : (
                <div className="space-y-3">{active.map((r) => <JobCard key={r.id} r={r} />)}</div>
              )}
            </section>

            {completed.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">Completed</h2>
                <div className="space-y-3">{completed.map((r) => <JobCard key={r.id} r={r} done />)}</div>
              </section>
            )}
          </>
        )}
      </main>

      <Dialog open={!!podFor} onOpenChange={(o) => !o && closePod()}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Proof of Delivery</DialogTitle>
            <DialogDescription>
              {podFor?.work_order_no || podFor?.requisition_no} · {podFor?.delivery_location}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Received by — full name *</Label>
              <Input data-testid="pod-signed-by" className="mt-1.5 h-10" value={signedBy}
                onChange={(e) => setSignedBy(e.target.value)} placeholder="Recipient's full name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Signature *</Label>
              <div className="mt-1.5"><SignaturePad ref={sigRef} /></div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Delivery Photo</Label>
              {photo ? (
                <div className="mt-1.5 relative rounded-md overflow-hidden border border-border/60">
                  <img src={photo} alt="Delivery" className="w-full max-h-48 object-cover" data-testid="pod-photo-preview" />
                  <button type="button" data-testid="pod-photo-remove" onClick={() => setPhoto(null)}
                    className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 hover:bg-black/80">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  <Button type="button" variant="outline" data-testid="pod-camera-btn" className="gap-2 border-border/60"
                    onClick={() => cameraRef.current?.click()}>
                    <Camera className="h-4 w-4" /> Camera
                  </Button>
                  <Button type="button" variant="outline" data-testid="pod-gallery-btn" className="gap-2 border-border/60"
                    onClick={() => galleryRef.current?.click()}>
                    <ImageIcon className="h-4 w-4" /> Gallery
                  </Button>
                </div>
              )}
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pickPhoto} />
              <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={pickPhoto} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Delivery Notes</Label>
              <Textarea data-testid="pod-notes" className="mt-1.5" rows={2} value={notes}
                onChange={(e) => setNotes(e.target.value)} placeholder="Condition, exceptions, location left, etc." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePod}>Cancel</Button>
            <Button data-testid="pod-submit-btn" className="bg-[#0055FF] hover:bg-[#0044CC] gap-2"
              disabled={podMut.isPending} onClick={submitPodForm}>
              {podMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Submit & Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
